import React, { useContext, useEffect, useRef, useState } from "react";
import { Search, BookOpen, BookMarked, Calendar, Clock, AlertTriangle } from "lucide-react";
import axios from "axios";
import "../../../assets/css/gcstaff/staffbookspage/MyBooksPage.css";
import AppContext from "../../../context/AppContext";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ── Status badge config ── */
const statusConfig = {
  reserved: { cls: "gcTeacher-badge--pending",  label: "Reserved" },
  pending:  { cls: "gcTeacher-badge--pending",  label: "Pending"  },
  approved: { cls: "gcTeacher-badge--approved", label: "Approved" },
  rejected: { cls: "gcTeacher-badge--rejected", label: "Rejected" },
  issued:   { cls: "gcTeacher-badge--active",   label: "Issued"   },
  returned: { cls: "gcTeacher-badge--returned", label: "Returned" },
  overdue:  { cls: "gcTeacher-badge--overdue",  label: "Overdue"  },
};

const typeBadge = {
  issued:   "gcTeacher-type--issued",
  reserved: "gcTeacher-type--reserved",
};

/* ── Format date helper ── */
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/* ── Determine type from status ── */
const getType = (status) => {
  const s = (status || "").toLowerCase();
  return (s === "issued" || s === "returned" || s === "overdue") ? "issued" : "reserved";
};

/* ══════════════════════════════════════
   Single Book Row
══════════════════════════════════════ */
const BookRow = ({ booking, index }) => {
  const status  = (booking.status || "").toLowerCase();
  const type    = getType(status);
  const isIssued = type === "issued";
  const book    = booking.lbbook || {};

  const statusKey   = statusConfig[status] ? status : "reserved";
  const { cls, label } = statusConfig[statusKey];

  return (
    <div className="gcTeacher-book-row" style={{ "--row-index": index }}>

      {/* Icon */}
      <div className="gcTeacher-book-row__icon">
        {isIssued
          ? <BookOpen   size={20} strokeWidth={1.6} />
          : <BookMarked size={20} strokeWidth={1.6} />
        }
      </div>

      {/* Info */}
      <div className="gcTeacher-book-row__info">
        <h6 className="gcTeacher-book-row__title">{book.title || "—"}</h6>
        <p className="gcTeacher-book-row__author">{book.author || "—"}</p>

        <div className="gcTeacher-book-row__meta">
          {isIssued ? (
            <>
              <span className="gcTeacher-meta-chip">
                <Calendar size={11} strokeWidth={2} />
                Issued: {formatDate(booking.issue_date)}
              </span>
              <span className="gcTeacher-meta-chip">
                <Clock size={11} strokeWidth={2} />
                Due: {formatDate(booking.due_date)}
              </span>
            </>
          ) : (
            <span className="gcTeacher-meta-chip">
              <Calendar size={11} strokeWidth={2} />
              Reserved: {formatDate(booking.created_at)}
            </span>
          )}
        </div>
      </div>

      {/* Right badges */}
      <div className="gcTeacher-book-row__right">
        <span className={`gcTeacher-status-badge ${cls}`}>{label}</span>
        <span className={`gcTeacher-type-badge ${typeBadge[type]}`}>
          {type === "issued" ? "Issued" : "Reserved"}
        </span>
        {booking.fine && Number(booking.fine) > 0 && (
          <span className="gcTeacher-fine-chip">
            <AlertTriangle size={11} strokeWidth={2} />
            Fine: Rs. {booking.fine}
          </span>
        )}
      </div>

    </div>
  );
};

/* ══════════════════════════════════════
   My Books Page — Teacher
══════════════════════════════════════ */
const ITEMS_PER_PAGE = 10; // yahan se page size control hoti hai

const MyBooksPage = () => {
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);
  const [activeTab,     setActiveTab]     = useState("all");
  const [searchQuery,   setSearchQuery]   = useState("");
  const [allBookings,   setAllBookings]   = useState([]);
  const [loading,       setLoading]       = useState(false); // initial API fetch loading
  const [viewLoading,   setViewLoading]   = useState(false); // tab/page switch loading (sirf visible records k liye)
  const [error,         setError]         = useState("");
  const [currentPage,   setCurrentPage]   = useState(1); // pagination state

  // Pehli render (initial mount) par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  /* ── Fetch from API ── */
  const loadMyBookings = () => {
    if (!loggedInUser?.id) {
      setError("Teacher not logged in.");
      return;
    }

    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("teacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/booking/loadMyBookings`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success === true) {
          setAllBookings(res.data.bookings || []);
        } else {
          setAllBookings([]);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        setError("Failed to load bookings. Please try again.");
      });
  };

  useEffect(() => {
    if (loggedInUser?.id) {
      loadMyBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  /* ── Filter by tab & search ── */
  const getBooks = () => {
    // ✅ Expired reservations kabhi bhi kisi tab mein na dikhein (24hr auto-cancel)
    let books = allBookings.filter(b => (b.status || "").toLowerCase() !== "expired");

    if (activeTab === "issued") {
      books = books.filter(b => getType((b.status || "").toLowerCase()) === "issued");
    } else if (activeTab === "reserved") {
      books = books.filter(b => getType((b.status || "").toLowerCase()) === "reserved");
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      books = books.filter(b =>
        b.lbbook?.title?.toLowerCase().includes(q) ||
        b.lbbook?.author?.toLowerCase().includes(q)
      );
    }

    return books;
  };

  const tabs = [
    { key: "all",      label: "All Books" },
    { key: "issued",   label: "Issued"    },
    { key: "reserved", label: "Reserved"  },
  ];

  const filtered = getBooks();

  /* ── Pagination slice (sirf current page ka data list mein jayega) ── */
  const paginatedBooks = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Tab ya search change hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  /* ── Sirf visible records ki loading dikhayein:
     - Jab tab change ho (all/issued/reserved)
     - Jab pagination se dusre page par jayen
     Poori list ek sath "pop" nahi hogi, sirf jitne records is waqt
     screen par show hone hain unki hi loading chalegi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [activeTab, currentPage, searchQuery]);

  return (
    <div className="gcTeacher-mybooks">

      {/* ── Page Title ── */}
      <div className="gcTeacher-mybooks__header">
        <h2>My Books</h2>
      </div>

      {/* ── Tabs + Search ── */}
      <div className="gcTeacher-mybooks__toolbar">
        <div className="gcTeacher-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`gcTeacher-tab ${activeTab === tab.key ? "gcTeacher-tab--active" : ""}`}
              onClick={() => { setActiveTab(tab.key); setSearchQuery(""); }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="gcTeacher-search-wrap">
          <input
            type="text"
            className="gcTeacher-search-input"
            placeholder="Search books..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="gcTeacher-search-btn">
            <Search size={15} strokeWidth={2.2} />
          </button>
        </div>
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Books loading, Please wait..." />}

      {/* ── Error ── */}
      {!loading && error && (
        <div className="gcTeacher-book-list">
          <div className="gcTeacher-empty-state gcTeacher-empty-state--error">
            <AlertTriangle size={36} strokeWidth={1.2} />
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* ── Book List ── */}
      {!loading && !error && (
        <>
          <div className="gcTeacher-book-list">
            {viewLoading ? (
              /* ── Tab/page switch loading — sirf is section ki, baaki page affect nahi hota ── */
              <Loader message="Loading books..." />
            ) : filtered.length > 0 ? (
              paginatedBooks.map((booking, i) => (
                <BookRow key={booking.id} booking={booking} index={i} />
              ))
            ) : (
              <div className="gcTeacher-empty-state">
                <BookOpen size={38} strokeWidth={1.2} />
                <p>No books found.</p>
              </div>
            )}
          </div>

          {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

    </div>
  );
};

export default MyBooksPage;